/**
 * SPDX-License-Identifier: MPL-2.0
 * Copyright 2021 (c) Moritz Walker, ISW University of Stuttgart (for umati and VDW e.V.)
 * Copyright 2025-2026 (c) Miriam Mack, ISW University of Stuttgart (for umati and VDW e.V.)
 */

import Api, { type Company, type Machine, type OpcUaClient } from 'Api/Api';
import { useAuth } from 'Contexts/Auth';
import type React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { Button, Form, Modal, Table } from 'react-bootstrap';
import { BsBuilding, BsPencil, BsPlus, BsTrash } from 'react-icons/bs';
import { getApiErrorMessage } from '../components/apiError';
import { FeedbackAlert, useFeedback } from '../components/Feedback';
import styles from './Companies.module.css';

// Machine.ClientId is "<Company.ShortName>|<OpcUaClient.MqttUsername>", not the client's Id.
const clientIdOf = (company: Company, client: OpcUaClient) => `${company.ShortName}|${client.MqttUsername}`;

type MachineData = Record<string, unknown>;

const parseMachineData = (raw: Machine['Data']): MachineData | null => {
  if (!raw) return null;
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw) as MachineData;
    } catch {
      return null;
    }
  }
  return raw as MachineData;
};

// OPC UA values arrive either plain or wrapped as { Value, UaType } and LocalizedText as { Text | text, Locale }.
const asText = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return '-';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    for (const key of ['Value', 'Text', 'text']) {
      if (key in obj) return asText(obj[key]);
    }
  }
  return JSON.stringify(value);
};

type CompanySidebarProps = {
  companies: Company[];
  selectedCompanyId: string | undefined;
  setSelectedCompanyId: (companyId: string | undefined) => void;
  onAdd: () => void;
  canAdd: boolean;
};

const CompanySidebar = ({ companies, selectedCompanyId, setSelectedCompanyId, onAdd, canAdd }: CompanySidebarProps) => (
  <>
    <header className={styles['header-row']}>
      <h2 className="header">
        <BsBuilding className="icon" />
        Companies
      </h2>
    </header>
    <nav className={styles['companies-overview']} aria-label="Companies">
      <ul>
        {companies.map((company) => (
          <li key={company.Id}>
            <button
              type="button"
              className={styles['company-button']}
              aria-current={selectedCompanyId === company.Id}
              onClick={() => setSelectedCompanyId(company.Id)}
            >
              {company.Name}
            </button>
          </li>
        ))}
      </ul>
    </nav>
    {canAdd && (
      <Button size="sm" variant="outline-primary" className="w-100 mt-2" onClick={onAdd}>
        <BsPlus /> Add company
      </Button>
    )}
  </>
);

type CompanyHeaderProps = {
  company: Company;
  canEdit: boolean;
  onCompanyUpdate: (id: string, updatedData: Partial<Company>) => void;
};

const CompanyHeader = ({ company, canEdit, onCompanyUpdate }: CompanyHeaderProps) => {
  const [showEdit, setShowEdit] = useState(false);
  const [name, setName] = useState(company.Name ?? '');
  const [shortName, setShortName] = useState(company.ShortName ?? '');
  const [showErrors, setShowErrors] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  useEffect(() => {
    setName(company.Name ?? '');
    setShortName(company.ShortName ?? '');
  }, [company]);

  const nameError = name.trim() ? null : 'Please enter a company name.';
  const shortNameError = !shortName.trim()
    ? 'Please enter a short name.'
    : /\s|\|/.test(shortName.trim())
      ? 'The short name must not contain spaces or "|".'
      : null;
  const isValid = !nameError && !shortNameError;
  const isDirty = name.trim() !== (company.Name ?? '') || shortName.trim() !== (company.ShortName ?? '');
  const shortNameChanged = shortName.trim() !== (company.ShortName ?? '');

  const close = () => {
    setShowEdit(false);
    setShowErrors(false);
    setServerError(null);
    setName(company.Name ?? '');
    setShortName(company.ShortName ?? '');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setShowErrors(true);
    setServerError(null);
    if (!isValid || !company.Id) return;
    const updated = { Name: name.trim(), ShortName: shortName.trim() };
    try {
      await Api.Company.editCompany(company.Id, { ...company, ...updated });
      onCompanyUpdate(company.Id, updated);
      close();
    } catch (err) {
      setServerError(getApiErrorMessage(err, 'The company could not be saved.'));
    }
  };

  return (
    <>
      <div className={styles['name-header']}>
        <h4 className="mb-0">{company.Name}</h4>
        {canEdit && (
          <Button size="sm" variant="outline-primary" onClick={() => setShowEdit(true)}>
            <BsPencil className="me-1" />
            Edit
          </Button>
        )}
      </div>
      <p className={styles['name-subheading']}>Short name: {company.ShortName}</p>

      <Modal show={showEdit} onHide={close}>
        <Modal.Header closeButton>
          <Modal.Title>Edit company</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit} noValidate>
          <Modal.Body>
            <Form.Group className="mb-3" controlId="company-name">
              <Form.Label>Name</Form.Label>
              <Form.Control
                value={name}
                onChange={(e) => setName(e.target.value)}
                isInvalid={showErrors && !!nameError}
                autoFocus
              />
              <Form.Control.Feedback type="invalid">{nameError}</Form.Control.Feedback>
            </Form.Group>
            <Form.Group className="mb-2" controlId="company-short-name">
              <Form.Label>Short name</Form.Label>
              <Form.Control
                value={shortName}
                onChange={(e) => setShortName(e.target.value)}
                isInvalid={showErrors && !!shortNameError}
              />
              <Form.Control.Feedback type="invalid">{shortNameError}</Form.Control.Feedback>
              {shortNameChanged && !shortNameError && (
                <Form.Text className="text-warning">
                  The short name is part of the machine identifiers of this company. Changing it can disconnect existing
                  machines from their clients.
                </Form.Text>
              )}
            </Form.Group>
            {serverError && <div className="alert alert-danger py-2 mt-3 mb-0">{serverError}</div>}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={close}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={!isDirty}>
              Save
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  );
};

type ClientFormValues = { Username: string; Comment: string };

type ClientFormModalProps = {
  show: boolean;
  title: string;
  submitLabel: string;
  initial: ClientFormValues;
  existingUsernames: string[];
  usernameLocked?: boolean;
  onClose: () => void;
  onSubmit: (values: ClientFormValues) => Promise<void>;
};

const ClientFormModal = ({
  show,
  title,
  submitLabel,
  initial,
  existingUsernames,
  usernameLocked = false,
  onClose,
  onSubmit,
}: ClientFormModalProps) => {
  const [values, setValues] = useState<ClientFormValues>(initial);
  const [showErrors, setShowErrors] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (show) {
      setValues(initial);
      setShowErrors(false);
      setServerError(null);
    }
  }, [show, initial]);

  const username = values.Username.trim();
  const usernameError = !username
    ? 'Please enter a username.'
    : /\s|\|/.test(username)
      ? 'The username must not contain spaces or "|".'
      : !usernameLocked && existingUsernames.some((u) => u.toLowerCase() === username.toLowerCase())
        ? 'A client with this username already exists in this company.'
        : null;
  const commentError = values.Comment.trim()
    ? null
    : 'Please enter a short description, e.g. the machine hall or the purpose.';
  const isValid = !usernameError && !commentError;
  const isDirty = username !== initial.Username || values.Comment.trim() !== initial.Comment;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setShowErrors(true);
    setServerError(null);
    if (!isValid) return;
    setSubmitting(true);
    try {
      await onSubmit({ Username: username, Comment: values.Comment.trim() });
    } catch (err) {
      setServerError(getApiErrorMessage(err, 'The client could not be saved.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={onClose}>
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit} noValidate>
        <Modal.Body>
          <Form.Group className="mb-3" controlId="client-username">
            <Form.Label>MQTT username</Form.Label>
            <Form.Control
              value={values.Username}
              onChange={(e) => setValues((v) => ({ ...v, Username: e.target.value }))}
              isInvalid={showErrors && !!usernameError}
              disabled={usernameLocked}
              autoFocus={!usernameLocked}
            />
            {usernameLocked ? (
              <Form.Text muted>The username cannot be changed because machines are identified by it.</Form.Text>
            ) : (
              <Form.Text muted>Used by the machine or gateway to log in to the MQTT broker.</Form.Text>
            )}
            <Form.Control.Feedback type="invalid">{usernameError}</Form.Control.Feedback>
          </Form.Group>
          <Form.Group className="mb-2" controlId="client-comment">
            <Form.Label>Description</Form.Label>
            <Form.Control
              value={values.Comment}
              onChange={(e) => setValues((v) => ({ ...v, Comment: e.target.value }))}
              isInvalid={showErrors && !!commentError}
              autoFocus={usernameLocked}
            />
            <Form.Control.Feedback type="invalid">{commentError}</Form.Control.Feedback>
          </Form.Group>
          {serverError && <div className="alert alert-danger py-2 mt-3 mb-0">{serverError}</div>}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={submitting || !isDirty}>
            {submitting ? 'Saving…' : submitLabel}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

type CompanyClientsProps = {
  company: Company;
  clients: OpcUaClient[];
  canEdit: boolean;
  onClientsChange: (update: (prev: OpcUaClient[]) => OpcUaClient[]) => void;
};

const CompanyClients = ({ company, clients, canEdit, onClientsChange }: CompanyClientsProps) => {
  const [showAdd, setShowAdd] = useState(false);
  const [editClient, setEditClient] = useState<OpcUaClient | null>(null);
  const [clientToDelete, setClientToDelete] = useState<OpcUaClient | null>(null);
  const [createdPassword, setCreatedPassword] = useState<{ username: string; password: string } | null>(null);
  const { feedback, success, error } = useFeedback();

  const existingUsernames = clients.map((c) => c.MqttUsername ?? '');
  const emptyValues = useMemo<ClientFormValues>(() => ({ Username: '', Comment: '' }), []);
  const editValues = useMemo<ClientFormValues>(
    () => ({ Username: editClient?.MqttUsername ?? '', Comment: editClient?.Comment ?? '' }),
    [editClient],
  );

  const handleAdd = async (values: ClientFormValues) => {
    if (!company.Id) return;
    const { data } = await Api.OpcUaClient.addClient(company.Id, values);
    const created = data as OpcUaClient & { Password?: string };
    onClientsChange((prev) => [...prev, created]);
    setShowAdd(false);
    if (created.Password) {
      setCreatedPassword({ username: created.MqttUsername ?? values.Username, password: created.Password });
    } else {
      success('Client created.');
    }
  };

  const handleEdit = async (values: ClientFormValues) => {
    if (!editClient?.Id) return;
    await Api.OpcUaClient.modifyClient(editClient.Id, values);
    onClientsChange((prev) => prev.map((c) => (c.Id === editClient.Id ? { ...c, Comment: values.Comment } : c)));
    setEditClient(null);
    success('Client updated.');
  };

  const handleDelete = async () => {
    const client = clientToDelete;
    setClientToDelete(null);
    if (!client?.Id) return;
    try {
      await Api.OpcUaClient.deleteClient(client.Id);
      onClientsChange((prev) => prev.filter((c) => c.Id !== client.Id));
      success('Client deleted.');
    } catch (err) {
      error(err, 'The client could not be deleted.');
    }
  };

  return (
    <section className="mt-4">
      <div className="d-flex align-items-center justify-content-between mb-2">
        <h5 className="mb-0">Clients</h5>
        {canEdit && (
          <Button size="sm" variant="outline-primary" onClick={() => setShowAdd(true)}>
            <BsPlus /> Add client
          </Button>
        )}
      </div>
      <p className="text-muted small">A client is the MQTT login a machine or gateway uses to send data.</p>

      <FeedbackAlert feedback={feedback} />

      <Table bordered hover size="sm" className={styles.table}>
        <thead>
          <tr>
            <th style={{ width: '30%' }}>MQTT username</th>
            <th>Description</th>
            {canEdit && <th style={{ width: 90 }}></th>}
          </tr>
        </thead>
        <tbody>
          {clients.length === 0 ? (
            <tr>
              <td colSpan={canEdit ? 3 : 2} className="text-center text-muted">
                No clients yet
              </td>
            </tr>
          ) : (
            clients.map((client) => (
              <tr key={client.Id}>
                <td className="align-middle">
                  <code>{client.MqttUsername}</code>
                </td>
                <td className="align-middle">{asText(client.Comment)}</td>
                {canEdit && (
                  <td className="align-middle text-nowrap">
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      className="me-1"
                      onClick={() => setEditClient(client)}
                      aria-label={`Edit client ${client.MqttUsername}`}
                    >
                      <BsPencil />
                    </Button>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => setClientToDelete(client)}
                      aria-label={`Delete client ${client.MqttUsername}`}
                    >
                      <BsTrash />
                    </Button>
                  </td>
                )}
              </tr>
            ))
          )}
        </tbody>
      </Table>

      <ClientFormModal
        show={showAdd}
        title="Add client"
        submitLabel="Add client"
        initial={emptyValues}
        existingUsernames={existingUsernames}
        onClose={() => setShowAdd(false)}
        onSubmit={handleAdd}
      />

      <ClientFormModal
        show={editClient !== null}
        title="Edit client"
        submitLabel="Save"
        initial={editValues}
        existingUsernames={existingUsernames}
        usernameLocked
        onClose={() => setEditClient(null)}
        onSubmit={handleEdit}
      />

      <Modal show={createdPassword !== null} onHide={() => setCreatedPassword(null)} centered backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>Client created</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Save the password now. It is shown only once and cannot be retrieved later.</p>
          <dl className="row mb-0">
            <dt className="col-4">Username</dt>
            <dd className="col-8">
              <code>{createdPassword?.username}</code>
            </dd>
            <dt className="col-4">Password</dt>
            <dd className="col-8">
              <code className="user-select-all">{createdPassword?.password}</code>
            </dd>
          </dl>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={() => createdPassword && navigator.clipboard?.writeText(createdPassword.password)}
          >
            Copy password
          </Button>
          <Button variant="primary" onClick={() => setCreatedPassword(null)}>
            I have saved it
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={clientToDelete !== null} onHide={() => setClientToDelete(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Delete client</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Do you really want to delete the client <strong>{clientToDelete?.MqttUsername}</strong>? Machines using this
          login can no longer send data.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setClientToDelete(null)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </section>
  );
};

const CompanyMachines = ({ machines }: { machines: Machine[] }) => {
  const sorted = [...machines].sort((a, b) => {
    const aNum = Number.parseInt(a.ShortId?.replace(/\D+/g, '') || '0', 10);
    const bNum = Number.parseInt(b.ShortId?.replace(/\D+/g, '') || '0', 10);
    return aNum - bNum;
  });

  return (
    <section className="mt-4">
      <h5>Machines</h5>
      {sorted.length === 0 ? (
        <div className="text-muted">No machines yet</div>
      ) : (
        <Table bordered hover responsive size="sm" className={styles.table}>
          <thead>
            <tr>
              <th>Short ID</th>
              <th>Type</th>
              <th>Client</th>
              <th>Product instance URI</th>
              <th>Location</th>
              <th>Manufacturer</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((machine) => {
              const data = parseMachineData(machine.Data);
              return (
                <tr key={machine.Id}>
                  <td>{asText(machine.ShortId)}</td>
                  <td>{asText(machine.TypeDefinition)}</td>
                  <td>{asText(machine.ClientId)}</td>
                  <td>{asText(data?.ProductInstanceUri)}</td>
                  <td>{asText(data?.Location)}</td>
                  <td>{asText(data?.Manufacturer)}</td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      )}
    </section>
  );
};

type AddCompanyModalProps = {
  show: boolean;
  existing: Company[];
  onClose: () => void;
  onCreated: (company: Company) => void;
};

const AddCompanyModal = ({ show, existing, onClose, onCreated }: AddCompanyModalProps) => {
  const [name, setName] = useState('');
  const [shortName, setShortName] = useState('');
  const [showErrors, setShowErrors] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const nameError = !name.trim()
    ? 'Please enter a company name.'
    : existing.some((c) => (c.Name ?? '').toLowerCase() === name.trim().toLowerCase())
      ? 'A company with this name already exists.'
      : null;
  const shortNameError = !shortName.trim()
    ? 'Please enter a short name.'
    : /\s|\|/.test(shortName.trim())
      ? 'The short name must not contain spaces or "|".'
      : existing.some((c) => (c.ShortName ?? '').toLowerCase() === shortName.trim().toLowerCase())
        ? 'This short name is already in use.'
        : null;
  const isValid = !nameError && !shortNameError;

  const close = () => {
    setName('');
    setShortName('');
    setShowErrors(false);
    setServerError(null);
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setShowErrors(true);
    setServerError(null);
    if (!isValid) return;
    setSubmitting(true);
    try {
      const { data: created } = await Api.Company.addCompany({ Name: name.trim(), ShortName: shortName.trim() });
      onCreated(created);
      close();
    } catch (err) {
      setServerError(getApiErrorMessage(err, 'The company could not be created.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={close}>
      <Modal.Header closeButton>
        <Modal.Title>Add company</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit} noValidate>
        <Modal.Body>
          <Form.Group className="mb-3" controlId="add-company-name">
            <Form.Label>Name</Form.Label>
            <Form.Control
              value={name}
              onChange={(e) => setName(e.target.value)}
              isInvalid={showErrors && !!nameError}
              autoFocus
            />
            <Form.Control.Feedback type="invalid">{nameError}</Form.Control.Feedback>
          </Form.Group>
          <Form.Group className="mb-2" controlId="add-company-short-name">
            <Form.Label>Short name</Form.Label>
            <Form.Control
              value={shortName}
              onChange={(e) => setShortName(e.target.value)}
              isInvalid={showErrors && !!shortNameError}
              placeholder="e.g. vdma"
            />
            <Form.Text muted>Short, lowercase, no spaces. It becomes part of the machine identifiers.</Form.Text>
            <Form.Control.Feedback type="invalid">{shortNameError}</Form.Control.Feedback>
          </Form.Group>
          {serverError && <div className="alert alert-danger py-2 mt-3 mb-0">{serverError}</div>}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={close}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={submitting}>
            {submitting ? 'Adding…' : 'Add company'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

const CompanyPage = () => {
  const auth = useAuth();
  const roles = auth.user?.Roles ?? [];
  const isSuperAdmin = roles.includes('SuperAdmin');
  const canEdit = isSuperAdmin || roles.includes('Admin');

  const [companies, setCompanies] = useState<Company[]>([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string | undefined>(undefined);
  const [clients, setClients] = useState<OpcUaClient[]>([]);
  const [allMachines, setAllMachines] = useState<Machine[]>([]);
  const [showAddCompany, setShowAddCompany] = useState(false);
  const [showDeleteCompany, setShowDeleteCompany] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const pageFeedback = useFeedback();

  useEffect(() => {
    if (!auth.user) return;

    Api.Company.getCompanies()
      .then(({ data }) => {
        const sorted = [...data].sort((a, b) => (a.Name ?? '').localeCompare(b.Name ?? ''));
        setCompanies(sorted);
        setSelectedCompanyId((current) => current ?? sorted[0]?.Id);
      })
      .catch((err) => {
        const status = (err as { response?: { status?: number } })?.response?.status;
        if (!canEdit && (status === 401 || status === 403)) {
          setCompanies([]);
          return;
        }
        setLoadError(getApiErrorMessage(err, 'The companies could not be loaded.'));
      });

    Api.Machines.getMachines()
      .then(({ data }) => setAllMachines(data))
      .catch(() => setAllMachines([]));
  }, [auth.user, canEdit]);

  useEffect(() => {
    if (!selectedCompanyId) return;
    let cancelled = false;
    setClients([]);
    Api.OpcUaClient.getClientsOfCompany(selectedCompanyId)
      .then(({ data }) => {
        if (!cancelled) setClients(data);
      })
      .catch(() => {
        if (!cancelled) setClients([]);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedCompanyId]);

  const selectedCompany = companies.find((c) => c.Id === selectedCompanyId);

  const companyMachines = useMemo(() => {
    if (!selectedCompany) return [];
    const ids = new Set(clients.map((c) => clientIdOf(selectedCompany, c)));
    return allMachines.filter((m) => m.ClientId && ids.has(m.ClientId));
  }, [selectedCompany, clients, allMachines]);

  const handleCompanyUpdate = (id: string, updated: Partial<Company>) => {
    setCompanies((prev) => prev.map((c) => (c.Id === id ? { ...c, ...updated } : c)));
  };

  const handleCreated = (created: Company) => {
    setCompanies((prev) => [...prev, created].sort((a, b) => (a.Name ?? '').localeCompare(b.Name ?? '')));
    setSelectedCompanyId(created.Id);
  };

  const handleDeleteCompany = async () => {
    setShowDeleteCompany(false);
    const company = selectedCompany;
    if (!company?.Id) return;
    try {
      await Api.Company.deleteCompany(company.Id);
      setCompanies((prev) => {
        const remaining = prev.filter((c) => c.Id !== company.Id);
        setSelectedCompanyId(remaining[0]?.Id);
        return remaining;
      });
      pageFeedback.success(`Company "${company.Name}" deleted.`);
    } catch (err) {
      pageFeedback.error(err, 'The company could not be deleted.');
    }
  };

  return (
    <>
      <div className={styles['companies-page']}>
        <aside className={styles['companies-sidebar']}>
          <CompanySidebar
            companies={companies}
            selectedCompanyId={selectedCompanyId}
            setSelectedCompanyId={setSelectedCompanyId}
            onAdd={() => setShowAddCompany(true)}
            canAdd={isSuperAdmin}
          />
        </aside>

        <main className={styles['companies-content']}>
          {loadError && <div className="alert alert-danger">{loadError}</div>}
          <FeedbackAlert feedback={pageFeedback.feedback} />

          {!loadError && companies.length === 0 && (
            <div className="text-muted">
              <h5>No companies</h5>
              <p>
                {isSuperAdmin
                  ? 'Use "Add company" to create the first company.'
                  : 'You are not assigned to any company yet. Please contact an administrator.'}
              </p>
            </div>
          )}

          {selectedCompany && (
            <>
              <CompanyHeader company={selectedCompany} canEdit={canEdit} onCompanyUpdate={handleCompanyUpdate} />
              <CompanyClients
                key={selectedCompany.Id}
                company={selectedCompany}
                clients={clients}
                canEdit={canEdit}
                onClientsChange={(update) => setClients(update)}
              />
              <CompanyMachines machines={companyMachines} />

              {isSuperAdmin && (
                <div className="d-flex mt-4">
                  <Button
                    size="sm"
                    variant="outline-danger"
                    className="ms-auto"
                    onClick={() => setShowDeleteCompany(true)}
                  >
                    <BsTrash className="me-1" />
                    Delete company
                  </Button>
                </div>
              )}
            </>
          )}
        </main>
      </div>

      {isSuperAdmin && (
        <AddCompanyModal
          show={showAddCompany}
          existing={companies}
          onClose={() => setShowAddCompany(false)}
          onCreated={handleCreated}
        />
      )}

      <Modal show={showDeleteCompany} onHide={() => setShowDeleteCompany(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Delete company</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Do you really want to delete <strong>{selectedCompany?.Name}</strong>?
          </p>
          <p className="mb-0">
            All clients of this company are deleted as well, and its machines will no longer be shown in the dashboard.
            This cannot be undone.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteCompany(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeleteCompany}>
            Delete company
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default CompanyPage;
